import { zodResolver } from "@hookform/resolvers/zod";
import UserValidator from "Validators/Common/User";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { HTTPErrorCallback } from "Helpers/Error";
import { getPasswordSecurityVerifications } from "Helpers/Password";
import { clearMask } from "Helpers/Util";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import Checkbox from "Components/Admin/Form/Checkbox";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import SafePassword from "Components/Admin/SafePassword";
import ToggleStatus from "Components/Admin/ToggleStatus";

import * as S from "./styles";

const UserModal = ({ user, sponsorId, onClose, onSuccess }) => {
  const isEdit = !!user;
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Na criação, por padrão enviamos um e-mail para o usuário criar a senha.
  const [sendPasswordEmail, setSendPasswordEmail] = useState(true);
  const [userData, setUserData] = useState(null);

  const formSchema = z.object({
    full_name: UserValidator.full_name,
    email: UserValidator.email,
    phone: UserValidator.phone,
    role: UserValidator.role,
  });

  const methods = useForm({ resolver: zodResolver(formSchema) });
  const { register, setValue } = methods;

  useEffect(() => {
    if (isEdit && user?.id) {
      api
        .post("/users/get-user-by-id", { user_id: user.id })
        .then((response) => {
          const u = response.data;
          setUserData(u);
          setValue("full_name", u.full_name);
          setValue("email", u.email);
          // ⚠️ `|| ""` obrigatório: `phone` é MASCARADO e o react-input-mask faz
          // `value.split("")` no componentDidUpdate — com nulo, estoura
          // "Cannot read properties of null" e a PÁGINA INTEIRA fica branca
          // (não só o modal), sem mensagem nenhuma.
          setValue("phone", u.phone || "");
          setValue("role", u.role || "");
        })
        .catch((error) => HTTPErrorCallback(error));
    }
    // eslint-disable-next-line
  }, []);

  const refreshUser = () => {
    if (user?.id) {
      api
        .post("/users/get-user-by-id", { user_id: user.id })
        .then((response) => setUserData(response.data))
        .catch((error) => HTTPErrorCallback(error));
    }
    onSuccess();
  };

  // Valida força e confirmação da senha. Retorna false (e notifica) se inválida.
  const isPasswordValid = () => {
    const verifications = getPasswordSecurityVerifications(
      password,
      null,
      false,
    );
    if (
      !verifications.hasEnoughLength ||
      !verifications.hasNumber ||
      !verifications.hasUppercase ||
      !verifications.hasSpecialCharacters
    ) {
      showNotification(
        "danger",
        "Atenção!",
        "A senha não atende aos requisitos mínimos de segurança!",
      );
      return false;
    }

    if (password !== confirmPassword) {
      showNotification("danger", "Atenção!", "As senhas não conferem!");
      return false;
    }

    return true;
  };

  const submit = (data) => {
    // Na edição a senha é opcional: só valida se algo foi preenchido.
    // Na criação só valida quando o admin optou por definir a senha manualmente.
    const shouldValidatePassword = isEdit
      ? !!(password || confirmPassword)
      : !sendPasswordEmail;

    if (shouldValidatePassword && !isPasswordValid()) return;

    setLoading(true);
    if (data.phone) data.phone = clearMask(data.phone);

    if (isEdit) {
      if (password) data.password = password;

      api
        .post("/sponsors/update-user", { user_id: user.id, user: data })
        .then(() => {
          setLoading(false);
          showNotification(
            "success",
            "Sucesso!",
            "Usuário atualizado com sucesso!",
          );
          onSuccess();
          onClose();
        })
        .catch((error) => {
          setLoading(false);
          HTTPErrorCallback(error);
        });
    } else {
      data.sponsor_id = parseInt(sponsorId);
      data.send_password_email = sendPasswordEmail;
      if (!sendPasswordEmail) data.password = password;

      api
        .post("/sponsors/create-user", data)
        .then(() => {
          setLoading(false);
          showNotification(
            "success",
            "Sucesso!",
            "Usuário criado com sucesso!",
          );
          onSuccess();
          onClose();
        })
        .catch((error) => {
          setLoading(false);
          HTTPErrorCallback(error);
        });
    }
  };

  const showPasswordFields = isEdit || !sendPasswordEmail;

  return (
    <S.Overlay onClick={onClose}>
      <S.Modal onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle>
            {isEdit ? "Editar Usuário" : "Adicionar Usuário"}
          </S.ModalTitle>
          {isEdit && userData && (
            <ToggleStatus
              route="/sponsors/toggle-user-status/"
              item={userData}
              refresh={refreshUser}
            />
          )}
        </S.ModalHeader>

        <FormWrapper submit={submit} {...methods}>
          <S.FormGrid>
            <Input
              {...register("full_name")}
              type="text"
              label="Nome:"
              placeholder="Nome"
              required
            />
            <Input
              {...register("email")}
              type="email"
              label="E-mail:"
              placeholder="E-mail"
              required
              disabled={isEdit}
            />
            <Input
              {...register("phone")}
              type="text"
              label="Telefone:"
              placeholder="+55 (00) 00000-0000"
              required
              mask="+99 (99) 99999-9999"
            />
            <Input
              {...register("role")}
              type="text"
              label="Cargo:"
              placeholder="Cargo"
              required
            />
          </S.FormGrid>

          {!isEdit && (
            <Checkbox
              name="send_password_email"
              label="Enviar e-mail para criação de senha?"
              defaultChecked={true}
              onChange={(e) => setSendPasswordEmail(e.target.checked)}
            />
          )}

          {showPasswordFields && (
            <>
              <Input
                name="password"
                type="password"
                label="Criar senha:"
                placeholder={
                  isEdit ? "Deixe em branco para não alterar" : "Senha"
                }
                required={!isEdit}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Input
                name="confirm_password"
                type="password"
                label="Confirmar nova senha:"
                placeholder="Confirmar senha"
                required={!isEdit}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <SafePassword password={password} />
            </>
          )}

          <S.Buttons>
            <Button
              type="button"
              text="Cancelar"
              onClick={onClose}
              disabled={loading}
              width="150px"
            />
            <Button
              type="submit"
              text={
                loading ? "Salvando..." : isEdit ? "Atualizar" : "Criar Usuário"
              }
              disabled={loading}
              width="150px"
            />
          </S.Buttons>
        </FormWrapper>
      </S.Modal>
    </S.Overlay>
  );
};

export default UserModal;
